import { useEffect, useState } from "react";
import api from "../api";
import { Field, FileInput } from "./ui/Form";
import ExhibitA from "./ExhibitA";

/**
 * Warrant Returns.
 *
 * Previously two tools. The split was historical: the "online" variant existed
 * to dodge Render's 512MB limit by pushing the spatial work onto AGOL instead
 * of loading shapefiles into memory. That constraint is gone, but reading
 * parcels and addresses straight from AGOL is still the better default -- there
 * is nothing to download, and the layers cannot drift out of date.
 *
 * So it is one tool with a source switch. Uploading shapefiles remains
 * available for a one-off run against data that is not on AGOL.
 */

type Source = "agol" | "upload";
type Output = "csv" | "docx";

type LayerConfig = {
  parcels_item_id?: string;
  address_item_id?: string;
  is_configured?: boolean;
};

function RecordsCard() {
  const [source, setSource] = useState<Source>("agol");
  const [output, setOutput] = useState<Output>("csv");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [ownershipFile, setOwnershipFile] = useState<File | null>(null);
  const [parcelsFile, setParcelsFile] = useState<File | null>(null);
  const [addressFile, setAddressFile] = useState<File | null>(null);

  const [layers, setLayers] = useState<LayerConfig | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [parcelsOverride, setParcelsOverride] = useState("");
  const [addressOverride, setAddressOverride] = useState("");

  const [count, setCount] = useState<number | null>(null);
  const [busy, setBusy] = useState<"" | "count" | "generate">("");
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    api
      .get("/warrant-returns/sources")
      .then((r) => setLayers(r.data?.agol ?? null))
      .catch(() => setLayers(null));
  }, []);

  const datesValid = Boolean(startDate && endDate && startDate <= endDate);
  const filesReady =
    Boolean(ownershipFile) && (source === "agol" || (parcelsFile && addressFile));

  const handleCount = async () => {
    setBusy("count");
    setResult(null);
    setCount(null);
    try {
      const res = await api.post("/warrant-returns/count", {
        start_date: startDate,
        end_date: endDate,
        source,
      });
      setCount(res.data?.count ?? 0);
    } catch (err: any) {
      setResult({ status: "failure", message: err?.response?.data?.message || err.message });
    } finally {
      setBusy("");
    }
  };

  const handleGenerate = async () => {
    setBusy("generate");
    setResult(null);
    try {
      const form = new FormData();
      form.append("source", source);
      form.append("output", output);
      form.append("start_date", startDate);
      form.append("end_date", endDate);
      if (ownershipFile) form.append("ownership_file", ownershipFile);
      if (source === "upload") {
        if (parcelsFile) form.append("parcels_file", parcelsFile);
        if (addressFile) form.append("address_file", addressFile);
      } else {
        if (parcelsOverride.trim()) form.append("parcels_item_id", parcelsOverride.trim());
        if (addressOverride.trim()) form.append("address_item_id", addressOverride.trim());
      }

      const res = await api.post("/warrant-returns", form, { responseType: "blob" });

      const ext = output === "docx" ? "docx" : "csv";
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `warrant_returns_${startDate}_to_${endDate}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setResult({
        status: "success",
        message: `Downloaded ${res.headers["x-record-count"] ?? "?"} record(s).`,
      });
    } catch (err: any) {
      // A failed blob request carries its JSON error inside the blob body.
      let message = err.message;
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        try {
          message = JSON.parse(await blob.text()).message ?? message;
        } catch {
          /* keep the original message */
        }
      }
      setResult({ status: "failure", message });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-5">
      {/* Source */}
      <div>
        <div className="eyebrow mb-1.5">Parcels &amp; addresses from</div>
        <div className="flex bg-paper-2 rounded-sm2 p-0.5 text-[13px] font-semibold w-fit">
          {(
            [
              ["agol", "AGOL layers"],
              ["upload", "Uploaded shapefiles"],
            ] as [Source, string][]
          ).map(([val, label]) => (
            <button
              key={val}
              onClick={() => setSource(val)}
              className={`px-3 py-1 rounded-sm2 transition ${
                source === val ? "bg-card shadow-sm text-ink" : "text-ink-faint hover:text-ink-soft"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="text-ink-faint text-[12px] mt-1.5">
          {source === "agol"
            ? "Reads CMAD_Parcels and CMAD_Addresses straight from AGOL. Nothing to download."
            : "Use your own parcels and address shapefiles for this run."}
        </p>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="eyebrow">Start date</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="mt-1 w-full bg-card border border-line-strong rounded-sm2 px-3 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="eyebrow">End date</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="mt-1 w-full bg-card border border-line-strong rounded-sm2 px-3 py-1.5 text-sm"
          />
        </label>
      </div>
      {startDate && endDate && startDate > endDate && (
        <p className="text-[12px] text-red-600 -mt-3">End date is before the start date.</p>
      )}

      {/* Files */}
      <div className="space-y-3">
        <Field label="Ownership CSV" hint="Required either way — ownership data is not on AGOL.">
          <FileInput accept=".csv" file={ownershipFile} onFile={setOwnershipFile} disabled={busy !== ""} />
        </Field>

        {source === "upload" && (
          <div className="grid grid-cols-1 min-[640px]:grid-cols-2 gap-3 animate-[fadeIn_.2s_ease-out_both]">
            <Field label="Parcels shapefile (.zip)">
              <FileInput accept=".zip" file={parcelsFile} onFile={setParcelsFile} disabled={busy !== ""} />
            </Field>
            <Field label="Address shapefile (.zip)">
              <FileInput accept=".zip" file={addressFile} onFile={setAddressFile} disabled={busy !== ""} />
            </Field>
          </div>
        )}
      </div>

      {/* AGOL layer overrides */}
      {source === "agol" && (
        <div className="rounded-sm2 border border-line-strong p-3">
          <button
            onClick={() => setShowAdvanced((v) => !v)}
            className="text-[13px] font-semibold text-ink-soft hover:text-ink"
          >
            {showAdvanced ? "▾" : "▸"} Layer IDs
            <span className="text-ink-faint font-normal"> — defaults are already set</span>
          </button>
          {showAdvanced && (
            <div className="mt-3 space-y-2 animate-[fadeIn_.2s_ease-out_both]">
              <label className="block">
                <span className="eyebrow">Parcels layer item ID</span>
                <input
                  value={parcelsOverride}
                  onChange={(e) => setParcelsOverride(e.target.value)}
                  placeholder={layers?.parcels_item_id ?? "default"}
                  className="mt-1 w-full bg-card border border-line-strong rounded-sm2 px-3 py-1.5 text-sm font-mono"
                />
              </label>
              <label className="block">
                <span className="eyebrow">Address layer item ID</span>
                <input
                  value={addressOverride}
                  onChange={(e) => setAddressOverride(e.target.value)}
                  placeholder={layers?.address_item_id ?? "default"}
                  className="mt-1 w-full bg-card border border-line-strong rounded-sm2 px-3 py-1.5 text-sm font-mono"
                />
              </label>
              <p className="text-ink-faint text-[12px]">
                Leave blank to use the defaults above. An override applies to this
                server until it is restarted.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={handleCount}
          disabled={!datesValid || busy !== ""}
          className="btn-ghost"
        >
          {busy === "count" ? "Counting…" : "Count first"}
        </button>
        <button
          onClick={handleGenerate}
          disabled={!datesValid || !filesReady || busy !== ""}
          className="btn-primary"
        >
          {busy === "generate" ? "Generating…" : "Generate"}
        </button>

        <div className="flex bg-paper-2 rounded-sm2 p-0.5 text-[13px] font-semibold ml-auto">
          {(
            [
              ["csv", "CSV"],
              ["docx", "Word"],
            ] as [Output, string][]
          ).map(([val, label]) => (
            <button
              key={val}
              onClick={() => setOutput(val)}
              className={`px-3 py-1 rounded-sm2 transition ${
                output === val ? "bg-card shadow-sm text-ink" : "text-ink-faint hover:text-ink-soft"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {count !== null && (
        <p className="text-sm">
          <strong>{count}</strong> warrant inspection{count === 1 ? "" : "s"} in that range.
          {count === 0 && " Nothing to generate."}
        </p>
      )}

      {busy === "generate" && (
        <p className="text-ink-soft text-[13px]">
          This can take several minutes on a wide date range — the spatial joins are the slow part.
        </p>
      )}

      {result && (
        <div
          className={`rounded-sm2 p-3 text-sm ${
            result.status === "success"
              ? "bg-brand-tint text-brand-deep"
              : "bg-red-50 border border-red-200 text-red-800"
          }`}
        >
          {result.message}
        </div>
      )}
    </div>
  );
}

/**
 * Two ways into the same data. Exhibit A is the document the court receives, and
 * what most people come here for. All records is the raw list, every warrant row
 * with its activity, unfiltered, for checking what the rules did.
 */
export default function WarrantReturnsCard() {
  const [mode, setMode] = useState<"exhibit" | "records">("exhibit");
  return (
    <div className="space-y-5">
      <div className="flex bg-paper-2 rounded-sm2 p-0.5 text-[13px] font-semibold w-fit">
        {(
          [
            ["exhibit", "Exhibit A"],
            ["records", "All records (unfiltered)"],
          ] as ["exhibit" | "records", string][]
        ).map(([val, label]) => (
          <button
            key={val}
            onClick={() => setMode(val)}
            className={`px-3 py-1 rounded-sm2 transition ${
              mode === val ? "bg-card shadow-sm text-ink" : "text-ink-faint hover:text-ink-soft"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {mode === "exhibit" ? <ExhibitA /> : <RecordsCard />}
    </div>
  );
}
